import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CategoryType } from '@core/models/category-type.model';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractPackChannel } from '@core/models/contract-pack-channel/contract-pack-channel.model';
import { ContractPack } from '@core/models/contract-pack/contract-pack.model';
import { ContractThemaNego } from '@core/models/contract-thema-nego.model';
import { Contract } from '@core/models/contract.model';
import { FilteredItem } from '@core/models/filtered-item.model';
import { NegotiationType } from '@core/models/negotiation-type.model';
import { Target } from '@core/models/target.model';
import { ThemaChannelGoal } from '@core/models/thema-channel-goal/thema-channel-goal.model';
import { CategoryTypeService } from '@core/services/category-type/category-type.service';
import { ContractCategoryTypeService } from '@core/services/contract-category-type/contract-category-type.service';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { ContractPackService } from '@core/services/contract-pack/contract-pack.service';
import { ContractThemaNegoService } from '@core/services/contract-thema-nego/contract-thema-nego.service';
import { TargetService } from '@core/services/target.service';
import { ThemaChannelGoalService } from '@core/services/thema-channel-goal/thema-channel-goal.service';
import { TvSelectItemsDialogComponent } from '@shared/dialog/tv-select-items-dialog/tv-select-items-dialog.component';
import { TvSelectPackChannelsDialogComponent } from '@shared/dialog/tv-select-pack-channels-dialog/tv-select-pack-channels-dialog.component';
import moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
@Component({
  standalone: false,
  selector: 'app-thema-guaranteed-managing-dialog',
  templateUrl: './thema-guaranteed-managing-dialog.component.html',
  styleUrls: ['./thema-guaranteed-managing-dialog.component.scss'],
})
export class ThemaGuaranteedManagingDialogComponent implements OnInit, OnDestroy {
  public firstDayOfYear = moment().startOf('year');
  public lastDayOfYear = moment().endOf('year');

  public isTargetListLoading: boolean = false;
  public isContractPackListLoading: boolean = false;
  public isChannelListLoading: boolean = false;
  public isCategoryTypeLoading: boolean = false;
  public isItemLoading: boolean = false;
  public isSaving: boolean = false;
  public isEditing: boolean = false;
  public isGuarantee: boolean = true;

  public targetList: Target[];
  public contractPackList: ContractPack[];
  public contractPackChannelList: ContractPackChannel[];
  public categoryTypeList: CategoryType[];
  public itemList: any[] = [];

  public filteredTargetList: Observable<Target[]>;
  public filteredContractPackChannelList: Observable<ContractPackChannel[]>;
  public filteredItemList: Observable<any[]>;

  public negotiationDateForm: FormGroup;

  public targetFormControl = new FormControl(null, [Validators.required]);
  public budgetFormControl = new FormControl(null);
  public nctRateFormControl = new FormControl(null, [Validators.required, Validators.min(-999), Validators.max(0)]);
  public contractPackFormControl = new FormControl(null, [Validators.required]);
  public searchChannelFormControl = new FormControl(null);
  public channelRepartition = new FormControl(null, []);
  public categoryTypeFormControl = new FormControl(null);
  public searchFormControl = new FormControl(null);

  public editStartDate = '';
  public editEndDate = '';
  public isCategoryBlockAdded: boolean = false;
  public isChannelBlockAdded: boolean = false;
  public categoryConditionType: string = null;
  public categoryType = null;
  public isShowOption: boolean = false;
  public advertiserId = null;
  public contractYear = null;

  public selectedOptions: Set<any> = new Set();
  public selectAllOptions = false;
  public selectedChannels: Set<any> = new Set();
  public selectAllChannels = false;

  public channelsGoals$: Observable<ThemaChannelGoal[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialog,
    public snackBar: MatSnackBar,
    private targetService: TargetService,
    private contractPackService: ContractPackService,
    private categoryTypeService: CategoryTypeService,
    private contractCategoryTypeService: ContractCategoryTypeService,
    public contractThemaNegoService: ContractThemaNegoService,
    private themaChannelGoalService: ThemaChannelGoalService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
      themaNegotiationData: ContractThemaNego;
      isDuplication: boolean;
      isGuarantee: string;
    }
  ) {
    this.componentDestroyed$ = new Subject<void>();
  }

  // INIT

  ngOnInit(): void {
    this.initForm();
    this.initData();

    this.getTargetList();
    this.getCategoryTypeList();
    this.getContractPackList();
    this.getChannelsGoals();

    this.handlePackValueChange();
    this.handleChannelValueChange();
    this.handleCategoryTypeValueChange();
    this.handleItemValueChange();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  // INIT DATA
  public initData() {
    if (this.data) {
      this.advertiserId = this.data.contract?.advertiserId;
      this.contractYear = this.data.contract?.stateContractYear;

      this.isGuarantee = this.data.isGuarantee === 'guarantee';
      this.isEditing = !(this.data.themaNegotiationData === null);
    }
  }

  // INIT DIALOG
  private initDialog() {
    if (this.data.themaNegotiationData) {
      // Dates
      if (this.data.themaNegotiationData.startDate && this.data.themaNegotiationData.endDate) {
        this.negotiationDateForm?.get('startDate').setValue(this.data.themaNegotiationData.startDate);
        this.negotiationDateForm?.get('endDate').setValue(this.data.themaNegotiationData.endDate);
      }

      // Nct Rate
      if (this.data.themaNegotiationData.nctRate) {
        this.nctRateFormControl?.setValue(this.data.themaNegotiationData.nctRate);
      }

      // Value
      if (this.data.themaNegotiationData.value && this.isGuarantee) {
        this.budgetFormControl?.setValue(this.data.themaNegotiationData.value);
      }

      // Channels Conditions
      if (this.data.themaNegotiationData.channelConditions) {
        this.selectedChannels.clear();

        let contractPackChannel = new ContractPackChannel();
        if (!this.data.themaNegotiationData.contractPack) {
          this.data.themaNegotiationData.channelConditions.forEach(channelCondition => {
            let contractPackChannel = new ContractPackChannel({
              id: channelCondition.id,
              packId: null,
              channel: channelCondition.channel,
              rate: channelCondition.rate,
            });

            this.selectedChannels.add(contractPackChannel);
          });
        } else {
          this.data.themaNegotiationData.channelConditions.forEach(channelCondition => {
            let contractPackChannel = new ContractPackChannel(
              this.contractPackChannelList?.find(packChannel => packChannel.channel.id === channelCondition.channel.id)
            );

            contractPackChannel.rate = channelCondition.rate;
            this.selectedChannels.add(contractPackChannel);
          });
        }

        this.isChannelBlockAdded = this.selectedChannels.size > 0;
      }

      // Conditions
      if (this.data.themaNegotiationData.conditions) {
        this.selectedOptions.clear();

        this.data.themaNegotiationData.conditions.map(condition => {
          this.categoryConditionType = condition.isInclude === 1 ? 'Inclus' : 'Exclus';
          this.selectedOptions.add({
            categoryType: new CategoryType(condition.categoryType),
            categoryValue: {
              id: condition.categoryValue?.idCategoryValue,
              name: condition.categoryValue?.labelCategoryValue,
            },
          });
        });

        this.isCategoryBlockAdded = this.selectedOptions.size > 0;
      }
    }
  }

  // INITFORM

  public initForm() {
    this.firstDayOfYear = moment().year(Number(this.data?.contractItem.year)).startOf('year');
    this.lastDayOfYear = moment().year(Number(this.data?.contractItem.year)).endOf('year');

    this.negotiationDateForm = this.fb.group({
      startDate: [this.firstDayOfYear || null, [Validators.required]],
      endDate: [this.lastDayOfYear || null, [Validators.required]],
    });

    if (this.isGuarantee) {
      this.budgetFormControl.setValidators([Validators.required, Validators.min(0)]);
    }
  }

  // MANAGE TARGET CHOICE

  public getTargetList(name = '') {
    this.isTargetListLoading = true;
    const filter = {
      name: name,
    };

    this.targetService
      .getList(filter)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        targetList => {
          this.isTargetListLoading = false;
          this.targetList = targetList;
          if (this.data.themaNegotiationData) {
            const preselectedItem = this.targetList.find(item => item.id === this.data.themaNegotiationData.target.id);
            if (preselectedItem) {
              this.targetFormControl.setValue(preselectedItem);
            }
          }
          this.initFilteredTargetList();
        },
        error => {
          this.isTargetListLoading = false;
        }
      );
  }

  public initFilteredTargetList() {
    if (!this.isTargetListLoading) {
      this.filteredTargetList = this.targetFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => {
          return this._getLabel(value);
        }),
        map(label => {
          return label ? this._filter(label) : this.targetList.slice();
        })
      );
    }
  }

  private _getLabel(value: string | Target): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  /***
   * Fonction pour afficher le label dans l'input lors de la sélection
   * @param value
   * @private
   */
  public _filter(value: string = ''): Target[] {
    const filterValue = value.toLowerCase();
    return this.targetList.filter(target => {
      return target.id.toLowerCase().includes(filterValue) || target.name.toLowerCase().includes(filterValue);
    });
  }

  public displayTarget(target) {
    return target?.name && target?.id ? `${target.name} (${target.id})` : target;
  }

  // MANAGE PACK CHOICE

  public handlePackValueChange() {
    this.contractPackFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.selectedChannels.clear();

      this.contractPackChannelList = value?.contractPackChannels;
      value?.contractPackChannels?.forEach(contractPackChannel => {
        this.selectedChannels.add(contractPackChannel);
      });

      this.isChannelBlockAdded = this.selectedChannels.size > 0;
      this.initFilteredContractPackChannelList();
    });
  }

  public getContractPackList() {
    this.isContractPackListLoading = true;
    const filter = {
      year: this.contractYear,
    };

    this.contractPackService
      .getList(filter)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractPackList => {
          this.isContractPackListLoading = false;
          this.contractPackList = contractPackList;
          // Reload
          if (this.data.themaNegotiationData && this.data.themaNegotiationData.contractPack) {
            const preselectedContractPack = this.contractPackList.find(item => item.id === this.data.themaNegotiationData.contractPack.id);
            if (preselectedContractPack) {
              this.contractPackFormControl.setValue(preselectedContractPack);
              this.contractPackChannelList = preselectedContractPack.contractPackChannels;
            }
          }
          this.initDialog();
        },
        error => {
          this.isContractPackListLoading = false;
        }
      );
  }

  // MANAGE CHANNEL CHOICE

  public handleChannelValueChange() {
    this.searchChannelFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      if (typeof value !== 'string') {
        const alreadySelected = Array.from(this.selectedChannels).some((selectedItem: ContractPackChannel) => selectedItem.id === value.id);
        if (!alreadySelected) {
          this.isChannelBlockAdded = true;
          this.selectedChannels.add(value);
        } else {
          this.displaySnackBar('Certains items sont déjà sélectionnés.', 'tv-chip-error');
        }
      }
    });
  }

  public initFilteredContractPackChannelList() {
    if (!this.isTargetListLoading) {
      this.filteredContractPackChannelList = this.searchChannelFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => {
          return this._getContractPackChannelLabel(value);
        }),
        map(label => {
          return label ? this._filterContractPackChannel(label) : this.contractPackChannelList?.slice();
        })
      );
    }
  }

  public _getContractPackChannelLabel(value: string | ContractPackChannel): string {
    return typeof value === 'string' ? value : value?.channel.name || '';
  }

  /***
   * Fonction pour afficher le label dans l'input lors de la sélection
   * @param value
   * @private
   */
  public _filterContractPackChannel(value: string = ''): ContractPackChannel[] {
    const filterValue = value.toLowerCase();

    return this.contractPackChannelList.filter(contractPackChannel => {
      return (
        contractPackChannel.channel.id.toString() == filterValue || contractPackChannel.channel.name.toLowerCase().includes(filterValue)
      );
    });
  }

  public displayContractPackChannel(contractPackChannel) {
    if (contractPackChannel) {
      return contractPackChannel.channel?.name && contractPackChannel.channel?.id
        ? `${contractPackChannel.channel?.name}`
        : contractPackChannel;
    }
  }

  public removeChannelItem(item: any = null) {
    this.selectedChannels.delete(item);

    if (this.selectedChannels.size !== 0) {
      this.updateChannelsRate();
    } else {
      this.getContractPackList();
    }

    this.isChannelBlockAdded = !(this.selectedChannels.size === 0);
  }

  // MANAGE CATEGORY TYPE CHOICE

  public handleCategoryTypeValueChange() {
    this.categoryTypeFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.searchFormControl.setValue('');
      this.categoryType = value;
      this.getCategoryItemList();
    });
  }

  public getCategoryTypeList() {
    this.isCategoryTypeLoading = true;

    this.categoryTypeService
      .getCategoryTypeList({})
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        categoryType => {
          this.categoryTypeList = categoryType;
          this.isCategoryTypeLoading = false;

          this.deleteTargetCategoryType();
        },
        error => {
          this.isCategoryTypeLoading = false;
        }
      );
  }

  public deleteTargetCategoryType() {
    const filteredData = this.categoryTypeList.filter(item => item.name === 'Annonceur' || item.name === 'Produit');
    this.categoryTypeList = filteredData;
  }

  // MANAGE ITEM VALUE CHOICE
  public handleItemValueChange() {
    this.searchFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      if (typeof value !== 'string') {
        const alreadySelected = Array.from(this.selectedOptions).some((selectedItem: any) => selectedItem.categoryValue.id === value.id);
        if (!alreadySelected) {
          this.selectedOptions.add({
            categoryValue: value,
            categoryType: this.categoryTypeFormControl.value,
          });
        } else {
          this.displaySnackBar('Certains items sont déjà sélectionnés.', 'tv-chip-error');
        }
      }
    });
  }

  public displayItem(item: FilteredItem): string {
    return item?.name && item?.id ? `${item.name} (${item.id})` : '';
  }

  public _getItemLabel(value: string | NegotiationType): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public _itemFilter(value: string = ''): any[] {
    const filterValue = value.toLowerCase();
    return this.itemList.filter(item => item.id?.toString().includes(filterValue) || item.name?.toLowerCase().includes(filterValue));
  }

  public initFilteredItemList() {
    if (!this.isItemLoading) {
      this.filteredItemList = this.searchFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getItemLabel(value)),
        map(label => (label ? this._itemFilter(label) : this.itemList.slice()))
      );
    }
  }

  public getParamsByCategoryType(categoryType) {
    switch (categoryType.id) {
      case 1: // Advertiser
      case 4: // Product
        return { advertiserGroupId: this.advertiserId };
      case 6: // Campaign
        return { advertiserGroupId: this.advertiserId, year: this.contractYear };
      case 3: // Secodip
        return { year: this.contractYear };
      case 2: // Mandatory / Agency
      case 5: // Product Type
      case 7: // Target
      case 8: // Channel
      case 9: // Screen Type
      case 10: // Screen
      case 11: // Time Slot
      default:
        return {};
    }
  }

  public getCategoryItemList() {
    let params = this.getParamsByCategoryType(this.categoryType);

    params = Object.assign(params, {
      name: this.searchFormControl.value,
    });

    this.contractCategoryTypeService
      .getItemList(params, this.categoryType)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        items => {
          this.isItemLoading = false;
          this.itemList = items;

          this.initFilteredItemList();
        },
        error => {
          this.isItemLoading = false;
        }
      );
  }

  // MANAGE OPTION CHOICE

  public addCategoryBloc(conditionType: string): void {
    this.isCategoryBlockAdded = true;
    this.categoryConditionType = conditionType;
  }

  public mergedSelectedChannels(): ContractPackChannel[] {
    let mergedContractPackChannels: ContractPackChannel[] = [];

    this.contractPackChannelList.map(contractPackChannel => {
      let contratPackChannelItem: ContractPackChannel = { ...contractPackChannel };

      this.selectedChannels.forEach(selectedChannel => {
        if (selectedChannel.channel.id === contratPackChannelItem.channel.id) {
          contratPackChannelItem.rate = selectedChannel.rate;
          contratPackChannelItem.isSelected = true;
        }
      });

      mergedContractPackChannels.push(contratPackChannelItem);
    });

    return mergedContractPackChannels;
  }

  public listAllChannels(): void {
    this.searchChannelFormControl.setValue('');

    let data = {
      year: this.contractYear,
      itemList: of(this.mergedSelectedChannels()),
    };

    // Open Dialog
    const dialogRef = this.dialogRef.open(TvSelectPackChannelsDialogComponent, {
      data: data,
      panelClass: 'tv-dialog-container',
    });

    // After Closed Dialog
    dialogRef?.afterClosed().subscribe(items => {
      if (items && items.size > 0) {
        this.selectedChannels.clear();
        items.forEach(item => {
          this.selectedChannels.add(item);
          this.isChannelBlockAdded = true;
        });
      }
    });
  }

  public listAllItems(): void {
    this.searchFormControl.setValue('');

    let data = {
      itemList: of(this.itemList),
      itemListAlreadySelected: null,
    };

    // Open Dialog
    const dialogRef = this.dialogRef.open(TvSelectItemsDialogComponent, {
      data: data,
      panelClass: 'tv-dialog-container',
    });

    // After Closed Dialog
    dialogRef?.afterClosed().subscribe(items => {
      if (items && items.size > 0) {
        items.forEach(item => {
          let alreadySelected;
          alreadySelected = Array.from(this.selectedOptions).some((selectedItem: any) => selectedItem.categoryValue.id === item.id);
          if (!alreadySelected) {
            this.selectedOptions.add({
              categoryValue: item,
              categoryType: this.categoryTypeFormControl.value,
            });
          } else {
            this.displaySnackBar('Certains items sont déjà sélectionnés.', 'tv-chip-error');
          }
        });
      }
    });
  }

  public removeCondition() {
    this.selectedOptions.clear();
    this.isCategoryBlockAdded = false;
  }

  public removeItem(item: any = null) {
    this.selectedOptions.delete(item);
    if (this.selectedOptions.size === 0) this.isCategoryBlockAdded = false;
  }

  // MANAGE ACTION BUTTON

  public onCancelUpdateButtonClick() {
    this.isShowOption = false;
    this.displaySnackBar("L'ajout / modification a été annulée.", 'tv-chip-error');
    this.dialogRef.closeAll();
  }

  // SAVE

  getNegotiationDate() {
    let formattedStartDate = '';
    let formattedEndDate = '';

    const startDateValue = this.negotiationDateForm.get('startDate')?.value;
    const endDateValue = this.negotiationDateForm.get('endDate')?.value;

    if (startDateValue && typeof startDateValue !== 'string' && endDateValue && typeof endDateValue !== 'string') {
      formattedStartDate = moment(startDateValue).format('YYYY-MM-DD');
      formattedEndDate = moment(endDateValue).format('YYYY-MM-DD');
    } else if (startDateValue && typeof startDateValue === 'string' && endDateValue && typeof endDateValue === 'string') {
      let [day, month, year] = this.negotiationDateForm.get('startDate').value.split('/');
      formattedStartDate = `${year}-${month}-${day}`;
      [day, month, year] = this.negotiationDateForm.get('endDate').value.split('/');
      formattedEndDate = `${year}-${month}-${day}`;
    }

    return {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  }

  public getValueToSave() {
    if (
      this.targetFormControl?.valid &&
      this.negotiationDateForm?.valid &&
      this.budgetFormControl?.valid &&
      this.nctRateFormControl?.valid &&
      this.contractPackFormControl?.valid
    ) {
      this.isSaving = true;

      // Conditions
      let conditions = [];
      if (this.selectedOptions.size > 0) {
        for (const selectedOption of this.selectedOptions) {
          conditions.push({
            isInclude: this.categoryConditionType === 'Inclus',
            categoryType: selectedOption.categoryType,
            categoryValue: {
              id: selectedOption.categoryValue.id,
              name: selectedOption.categoryValue.name,
            },
          });
        }
      }

      // Channels
      let channels = [];
      if (this.selectedChannels.size > 0) {
        for (const selectedChannel of this.selectedChannels) {
          channels.push(new ContractPackChannel(selectedChannel));
        }
      }

      // Dates
      const negotiationDate = this.getNegotiationDate();

      return {
        id: !this.data.isDuplication ? (this.data?.themaNegotiationData?.id ? this.data.themaNegotiationData.id : null) : null,
        contractItemId: this.data.contractItem.id,
        isGuarantee: this.isGuarantee,
        targetCode: this.targetFormControl && this.targetFormControl.value ? this.targetFormControl.value.id : '',
        startDate: negotiationDate.startDate || '',
        endDate: negotiationDate.endDate || '',
        value: this.isGuarantee ? this.budgetFormControl.value : null,
        nctRate: this.nctRateFormControl.value,
        clientId: this.data.contract.id,
        stateContractYear: this.data.contract.stateContractYear,
        contractPack: this.contractPackFormControl.value,
        channelConditions: channels,
        conditions: conditions,
      };
    } else {
      this.markAllAsTouched();
      this.displaySnackBar('Veuillez remplir ou corriger le formulaire.', 'tv-chip-error');
    }
  }

  public markAllAsTouched() {
    this.targetFormControl.markAsTouched();
    this.negotiationDateForm.markAllAsTouched();
    this.budgetFormControl.markAsTouched();
    this.nctRateFormControl.markAsTouched();
    this.contractPackFormControl.markAsTouched();
  }

  onValidationButtonClick() {
    const data = this.getValueToSave();

    if (data) {
      if (data.id) {
        // UDPATE
        this.update(data);
      } else {
        // CREATE
        this.create(data);
      }
    }
  }

  create(data) {
    this.contractThemaNegoService.create(data).subscribe(
      rs => {
        this.updateContractCgvModel();
      },
      error => {
        this.isSaving = false;
      }
    );
  }

  update(data) {
    this.contractThemaNegoService.edit(data).subscribe(
      rs => {
        this.updateContractCgvModel();
      },
      error => {
        this.isSaving = false;
      }
    );
  }

  public updateContractCgvModel() {
    this.contractCgvModelUpdateService.updateContractCgvModel(this.data.contractItem?.id, this.data.contractItem).subscribe(
      response => {
        this.isSaving = false;
        this.displaySnackBar('Enregistrement terminé.', 'tv-chip-success');
        this.dialogRef.closeAll();
      },
      error => {
        this.displaySnackBar("Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support.", 'tv-chip-error');
        this.isSaving = false;
      }
    );
  }

  // OTHER
  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  public getTotalRate(): number {
    const rawSum = Array.from(this.selectedChannels).reduce((sum, selectedChannel) => sum + selectedChannel.rate, 0);

    return Math.round(rawSum * 10) / 10;
  }

  public getChannelsGoals(): void {
    this.themaChannelGoalService.getList(Number(this.data.contractItem?.year))?.subscribe((goals: ThemaChannelGoal[]) => {
      this.isChannelListLoading = false;
      this.channelsGoals$ = of(goals);
    });
  }

  public updateChannelsRate() {
    // get the sum of all the selected channels goals
    const totalSelectedGoals = this.channelsGoals$?.pipe(
      map(channels =>
        channels.reduce((sum, channelGoal) => {
          if (Array.from(this.selectedChannels).some(selectedChannel => selectedChannel.channel.channelCode === channelGoal.channelCode)) {
            return sum + channelGoal.goal;
          }
          return sum;
        }, 0)
      )
    );

    // recalculate the rate of the selected channels
    this.channelsGoals$?.subscribe(channelsGoals => {
      totalSelectedGoals.subscribe(total => {
        if (total > 0) {
          Array.from(this.selectedChannels).map(selectedChannel => {
            const channelGoal = channelsGoals.find(goal => goal.channelCode === selectedChannel.channel.channelCode);
            if (channelGoal) {
              // get the rate of the channel at 1 decimal
              selectedChannel.rate = Math.round((channelGoal.goal / total) * 10000) / 100;
            }

            return selectedChannel;
          });
        }

        // find if total is greater or lower than 100
        if (this.getTotalRate() !== 100 && this.selectedChannels.size > 0) {
          // find the channel with the highest rate and set the rate to match 100
          const maxRateChannel = Array.from(this.selectedChannels).reduce((prev, current) => (prev.rate > current.rate ? prev : current));

          const totalRate = this.getTotalRate();
          const diff = Math.round((totalRate > 100 ? totalRate - 100 : 100 - totalRate) * 10) / 10;

          maxRateChannel.rate = totalRate > 100 ? maxRateChannel.rate - diff : maxRateChannel.rate + diff;
        }
      });
    });
  }

  enforceNegativeNctRate(): void {
    const value = this.nctRateFormControl.value;
    if (value != null && !isNaN(value) && value > 0) {
      this.nctRateFormControl.setValue(-Math.abs(value));
    }
  }
}
